/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright (c) 2017 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the property of
 * Pearson Education, Inc. The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S.
 * and Foreign Patents, patent applications, and are protected by trade secret
 * or copyright law. Dissemination of this information, reproduction of this
 * material, and copying or distribution of this software is strictly forbidden
 * unless prior written permission is obtained from Pearson Education, Inc.
 */

import React from "react";
import PropTypes from "prop-types";
import styles from "./toggle.module.scss";
import Icon from "../Icon";
import { IntlProvider } from "react-intl";
import messages from "../messages";
import { generateId } from "./../_util/common.js";

class Toggle extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      checked: props.defaultChecked,
      hasFocus: false,
    };
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.isChecked !== undefined &&
      this.props.isChecked !== this.state.checked
    ) {
      this.setState({ checked: this.props.isChecked });
    }
  }

  swapCheckedState = () => {
    if (this.props.isChecked === undefined) {
      this.setState(
        {
          checked: !this.state.checked,
        },
        () => {
          console.log(this.state)
          this.props.onChange(this.state.checked);
        }
      );
    } else {
      this.props.onChange(!this.state.checked);
    }
  };

  handleToggleKeyEvents = (e) => {
    switch (e.key) {
      case "Enter": {
        e.preventDefault();
        this.swapCheckedState();
        break;
      }
      case "Space": {
        e.preventDefault();
        this.swapCheckedState();
        break;
      }
      default:
        break;
    }
  };

  handleClick = (e) => {
    if (this.props.onClickPreventDefault) {
      // this is to accomadate issues faced with hashedRouter appending to URL the event source name
      e.preventDefault();
    }
    this.swapCheckedState();
  };

  handleFocus = (event) => {
    const { onFocus } = this.props;
    if (onFocus) {
      onFocus(event);
    }
    this.setState({ hasFocus: true });
  };

  handleBlur = (event) => {
    const { onBlur } = this.props;
    if (onBlur) {
      onBlur(event);
    }
    this.setState({ hasFocus: false });
  };

  render() {
    return (
      <IntlProvider locale="en" messages={messages}>
        <label htmlFor={this.props.id} className={styles.ls_label}>
          {this.props.description}
        </label>
        <button
          value={this.props.value}
          className={`${styles.ls_toggle} 
            ${this.state.checked && `${styles.ls_toggle_checked}`}
            ${this.props.disabled && `${styles.ls_toggle_disabled}`}`}
          checked={this.state.checked}
          role="switch"
          id={this.props.id}
          aria-label={this.props.name}
          aria-checked={this.state.checked}
          onClick={this.handleClick}
          onKeyPress={this.handleToggleKeyEvents}
          disabled={this.props.disabled}
        >
          <Icon
            fileName="check-sm-18"
            name={"toggleOn-" + this.props.id}
            title="Checked"
          />
          <Icon
            fileName="remove-sm-18"
            name={"toggleOff-" + this.props.id}
            title="Unchecked"
          />
        </button>

        {this.props.isError && (
          <div className={styles.ls_error_message}>
            <span>
              <Icon
                fileName="warning-16"
                focusable="false"
                isHidden={true}
              />
              <label>{this.props.errorMessage}</label>
            </span>
          </div>
        )}
      </IntlProvider>
    );
  }
}

Toggle.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  name: PropTypes.string,
  description: PropTypes.string,
  defaultChecked: PropTypes.bool,
  isChecked: PropTypes.bool,
  disabled: PropTypes.bool,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  isError: PropTypes.bool,
  errorMessage: PropTypes.string,
  onClickPreventDefault: PropTypes.bool,
};

Toggle.defaultProps = {
  id: generateId(),
  value: "001",
  name: "toggle-01",
  disabled: false,
  defaultChecked: false,
  isError: false,
  errorMessage: "Default Error Message",
  onChange: () => {/**/},
  description: "",
  onClickPreventDefault: false,
};

export default Toggle;
